import React, { useEffect, useRef, useState } from 'react';
// import styled from 'react-emotion';
//import FrenzyColors from './FrenzyStyles';
import { DiagonalDirection, FrenzyXYTuple, LeptonDirection } from './Model';
import { areSameTuple, dedupe } from './Utils';

/** Lepton is (when scaled 1x):
 * 
 * a
 *  
 *   b
 *   
 *     c
 *
 *       d 
 * 
 */

/** Lepton is (when scaled 2x):
 * 
 * aa              
 * aa
 *
 * 
 *     bb
 *     bb
 *
 *
 *         cc
 *         cc
 * 
 * 
 *             dd
 *             dd
 */

type Props = {
	leptonId: number;
	x: number; // position of the HEAD of the thing
	y: number;
	direction: DiagonalDirection; // direction of the HEAD of the thing
	length: number;
	scale: number;
	speed: number;
	testID?: string;
	dying?: boolean;
	leptonDeathTick?: number;
}
const POSITION_MULTIPLIER = 2;
const buildDotArray = (props: Props):FrenzyXYTuple[] => {
	const { x, y, length, direction } = props;
	const oppositeDirection = LeptonDirection[direction.opposite];

	return Array.from({ length }, (d, i) => {
		return oppositeDirection.positionModifier({x, y}, i * POSITION_MULTIPLIER);
	});
}

// By prepending dots to what we already have, we get the "around the corner" bounce effect
// "for free"
const prependDotArray = (x: number, y: number, existingDots: FrenzyXYTuple[], direction: DiagonalDirection, speed: number):FrenzyXYTuple[] => {
	if (existingDots[0].x === x && existingDots[0].y === y) {
		return existingDots;
	}	
	const length = existingDots.length;

	// existingDots.splice(0,0, {x,y});

	// TODO: If the lepton is moving at greater than 1px per step,
	// we need to insert n points here to avoid the "stretched" look
	const oppositeDirection = LeptonDirection[direction.opposite];
	const newDots = Array.from({ length: speed }, (d, i) => {
		return oppositeDirection.positionModifier({x, y}, i * POSITION_MULTIPLIER);
	});

	// console.log(`New dots: ${JSON.stringify(newDots, null, 2)}`)

	existingDots.splice(0,0, ...newDots);

	return existingDots.slice(0, length);
}

function randomiseDot(dot: FrenzyXYTuple):FrenzyXYTuple {
	const xRand = Math.floor((Math.random() * 5) - 2);
	const yRand = Math.floor((Math.random() * 5) - 2);

	return {
		x: dot.x + xRand,
		y: dot.y + yRand
	}
}

function randomisePositions(dots: Array<FrenzyXYTuple>):Array<FrenzyXYTuple> {
	return dots.map(randomiseDot);
}

const Lepton : React.FunctionComponent<Props> = (props: Props) => {
	const { leptonId, x, y, speed, direction, length, scale, testID, dying, leptonDeathTick } = props; 


	const dots = useRef(buildDotArray(props)); // Creates the initial dots

	const uniqueDots = dedupe(dots.current, areSameTuple);

	useEffect(() => {
		if (dying) {
			const randomised = randomisePositions(uniqueDots);
			// console.log(`Re-rendering ${leptonId} while dying: dots were ${JSON.stringify(uniqueDots, null, 2)} now ${JSON.stringify(randomised, null, 2)}`)
			dots.current = randomised
		} else {
			dots.current = prependDotArray(x, y, dots.current, direction, speed);
		}
	}, [x, y, dying, leptonDeathTick]);

	return (
		<React.Fragment>
			{
				[...uniqueDots].map ( ({x, y}, i) =>
					<div key={`lepton-${leptonId}-dot-${i}`} className="LeptonDot" style={{ left: `${x}px`, top: `${y}px` }}></div>
				)
			}
		</React.Fragment>
	);		
}

export default Lepton;
